import React, { useEffect, useState } from 'react';

import { Helmet } from 'react-helmet';
import auth from '~/auth';
import { isAuthV2Enabled } from '~/auth/provider';

import { Alert, Box, Container, Typography } from '@mui/material';

import useAppNavigation from '~/hooks/useAppNavigation';

import ExpiredTokenMessage from './Auth/Messages/ExpiredTokenMessage';
import SocialGoogleButton from './Auth/SocialLogin/SocialGoogleButton';
import SsoSignInButton from './Auth/SocialLogin/SsoSignInButton';

function LoginPage() {
  const [errorMessage, setErrorMessage] = useState<string | undefined>();
  const [showExpiredTokenMessage, setShowExpiredTokenMessage] = useState(false);
  const [isProcessingCredential, setIsProcessingCredential] = useState(false);
  const { push } = useAppNavigation();
  const hasSsoParam = new URLSearchParams(window.location.search).get('sso') === 'true';
  const showSso = isAuthV2Enabled() && (window.configs.GOOGLE_LOGIN_ENABLED !== 'true' || hasSsoParam);
  const showGoogle =
    window.configs.GOOGLE_LOGIN_ENABLED === 'true' &&
    (window.google || window.configs.GOOGLE_AUTH_REDIRECT_DOMAIN) &&
    (!hasSsoParam || !isAuthV2Enabled());

  useEffect(() => {
    setShowExpiredTokenMessage(auth.consumeNotice()?.type === 'session-expired');
  }, []);

  // Handle credential from URL for redirect flows (review/canary apps using shared auth domain).
  // When redirected back from the shared auth domain, the URL contains ?credential=JWT.
  useEffect(() => {
    if (!new URLSearchParams(window.location.search).get('credential')) {
      return;
    }

    setIsProcessingCredential(true);

    void auth
      .completeLoginIfNeeded()
      .then(() => {
        push('/');
      })
      .catch((err) => {
        setIsProcessingCredential(false);
        setErrorMessage(err instanceof Error ? err.message : 'Google login failed');
      });
  }, [push]);

  // Show a loading state while processing credential from shared auth domain redirect
  if (isProcessingCredential) {
    return (
      <Container>
        <Box sx={{ mt: 6, textAlign: 'center' }}>
          <Typography>Signing in with Google...</Typography>
        </Box>
      </Container>
    );
  }

  return (
    <Container>
      {showExpiredTokenMessage && <ExpiredTokenMessage />}
      <Helmet>
        <title>Login</title>
      </Helmet>

      <Box className="row flex-centered">
        <Box sx={{ mt: 6 }}>
          <Typography variant="h1" className="page-header">
            Login
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, maxWidth: 360, marginTop: 2 }}>
            <Typography>Sign in to access the admin portal.</Typography>

            {errorMessage && <Alert severity="warning">{errorMessage}</Alert>}

            {showSso && <SsoSignInButton onError={setErrorMessage} />}

            {showGoogle && <SocialGoogleButton onError={setErrorMessage} />}
          </Box>
        </Box>
      </Box>
    </Container>
  );
}

export default LoginPage;
